import { AvatarWithTooltip, SkeletonBlock, Text } from "@zapier/design-system";
import { useCallback } from "react";
import { RelativeTime } from "@zapier/date-canvas";
import { useAtomValue, useSetAtom } from "jotai";
import {
  Canvas,
  sessionAtom,
  createNotificationSetter,
  Member,
} from "@zapier/canvas-state";

import useIsOwnerOrSuperAdmin from "../../hooks/useIsOwnerOrSuperAdmin";
import { deleteCanvas, DeleteCanvasErrorType } from "../../api/deleteCanvas";
import { ActionMenu } from "../../components/ActionMenu";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";

import {
  canvasesByPageAtomFamily_SUSPENDS,
  pageIndexAtom,
  refreshHigherVisitedPagesSetter,
} from "./canvasesByPageAtomFamily";
import styles from "./CanvasListPage.module.css";

export function CanvasListItem({
  canvas,
  owner,
  isOwnerLoading,
}: {
  canvas: Canvas;
  owner: Member | undefined;
  isOwnerLoading: boolean;
}) {
  const isAccountOwnerOrSuperAdmin = useIsOwnerOrSuperAdmin();
  const session = useAtomValue(sessionAtom);
  const createNotification = useSetAtom(createNotificationSetter);
  const { emitEventOnDeleteCanvas } = useEmitUserInteractionEvents();

  const pageIndex = useAtomValue(pageIndexAtom);
  const refreshCurrentPage = useSetAtom(
    canvasesByPageAtomFamily_SUSPENDS(pageIndex),
  );
  const refreshOtherVisitedPages = useSetAtom(refreshHigherVisitedPagesSetter);

  const onDeleteCanvas = useCallback(
    async (canvas: Canvas) => {
      const canvasName = canvas.name;

      try {
        const res = await deleteCanvas(canvas.id!);

        if (res.error) {
          throw res.error; // This throws the error object to be caught by the catch block
        }

        emitEventOnDeleteCanvas();

        // Refresh the list to reflect the deletion
        refreshCurrentPage();

        // Refresh other visited pages that have higher page indices because they may have been affected by the deletion
        refreshOtherVisitedPages();

        createNotification({
          type: "success",
          message: canvasName
            ? `Your "${canvasName}" canvas has been deleted.`
            : "Your canvas has been deleted.",
        });
      } catch (error) {
        const apiError = error as DeleteCanvasErrorType;

        let errorMessage = canvasName
          ? `We ran into an issue trying to delete your "${canvasName}" canvas. Please try again.`
          : "We ran into an issue trying to delete your canvas. Please try again.";

        if (apiError.json?.reason === "PROJECTS_API_ERROR") {
          errorMessage = `We couldn't delete your Canvas because we ran into an issue trying to delete it from the project.`;
        }

        createNotification({
          type: "error",
          message: errorMessage,
        });
      }
    },
    [
      createNotification,
      emitEventOnDeleteCanvas,
      refreshCurrentPage,
      refreshOtherVisitedPages,
    ],
  );

  return (
    <li key={canvas.id} className={styles.listItem}>
      <div className={styles.linkWrapper}>
        <a href={`/app/canvas/${canvas.id}`}>
          <div className={styles.linkRow}>
            <div className={styles.nameCol}>
              <Text type="Body3" color="TextDefault">
                {canvas.name || "untitled"}
              </Text>
            </div>
            <Text type="Body1" color="TextDefault">
              {canvas.node_count || canvas.nodes?.length || 0}
            </Text>
            <Text type="Body1" color="TextDefault">
              {canvas.lastchanged && (
                <RelativeTime
                  date={new Date(canvas.lastchanged)}
                  hasSuffix={true}
                />
              )}
            </Text>
            <div className={styles.ownerCell}>
              {isOwnerLoading && <SkeletonBlock height={25} width={25} />}
              {owner && (
                <AvatarWithTooltip
                  name={owner.full_name}
                  size="small"
                  isInteractive={true}
                  url={owner.photo_url}
                />
              )}
              {!isOwnerLoading && !owner && (
                <AvatarWithTooltip
                  name={"Unknown owner"}
                  size="small"
                  isInteractive={true}
                  url={null}
                />
              )}
            </div>
          </div>
        </a>
      </div>
      {isAccountOwnerOrSuperAdmin || session?.id === canvas.customuser_id ? (
        <div className={styles.actionMenu}>
          <ActionMenu deleteCanvas={() => onDeleteCanvas(canvas)} />
        </div>
      ) : (
        <div className={styles.actionMenuBlank}>
          <a href={`/app/canvas/${canvas.id}`}>
            <span className={styles.visuallyHidden}>Open Canvas</span>
          </a>
        </div>
      )}
    </li>
  );
}
